/**
 * READ A DOCUMENT'S TEXT IN THE BROWSER.
 *
 * The Library stores text, never files: `POST /knowledge/sources` takes
 * `sourceText`, and the column is immutable once written. So a PDF or a Word
 * document is turned into text HERE, in the uploader's own tab, and only that
 * text is sent. The file itself never leaves the browser.
 *
 * Both readers are loaded LAZILY — `pdfjs-dist` and `mammoth` are fetched the
 * first time someone drops a PDF or a .docx, and never otherwise. pdf.js runs
 * its parser in a same-origin worker (allowed by the admin CSP's `'self'`), and
 * `isEvalSupported: false` keeps it off `eval`, which that CSP forbids.
 *
 * What comes back is TEXT, and only text: `extractRawText`, never mammoth's HTML
 * conversion, so nothing in a document can reach the screen as markup.
 */

export type DocumentFormat = 'text' | 'pdf' | 'docx';

export type ExtractedText =
  | { readonly ok: true; readonly text: string; readonly pages?: number }
  | { readonly ok: false; readonly message: string };

/** Which reader a file needs, by extension, or `null` when we have none for it. */
export function documentFormatOf(filename: string): DocumentFormat | null {
  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  if (ext === '.txt' || ext === '.md' || ext === '.markdown') return 'text';
  if (ext === '.pdf') return 'pdf';
  if (ext === '.docx') return 'docx';
  return null;
}

/** The pdf.js surface used here. Injectable so a test can pass the Node (legacy) build. */
export interface PdfJs {
  getDocument(src: { data: Uint8Array; isEvalSupported?: boolean; verbosity?: number }): {
    promise: Promise<{
      numPages: number;
      getPage(n: number): Promise<{
        getTextContent(): Promise<{ items: readonly unknown[] }>;
      }>;
      destroy?(): Promise<void>;
    }>;
  };
}

/**
 * pdf.js and its worker, loaded once. `?worker` makes Vite emit the worker as a
 * `.js` asset: the admin nginx serves `.mjs` as `application/octet-stream`,
 * which `X-Content-Type-Options: nosniff` refuses to run.
 *
 * A FAILED load is forgotten, so the next PDF tries again: a dropped
 * connection while the chunk downloads must not break PDFs for the rest of the
 * tab's life.
 */
let browserLib: Promise<PdfJs> | null = null;
export function browserPdfJs(
  load: () => Promise<PdfJs> = async () => {
    const [pdfjs, { default: PdfWorker }] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?worker'),
    ]);
    pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
    return pdfjs as unknown as PdfJs;
  },
): Promise<PdfJs> {
  browserLib ??= load().catch((err: unknown) => {
    browserLib = null;
    throw err;
  });
  return browserLib;
}

/**
 * Every page's text layer, in reading order, pages separated by a blank line.
 * A PDF with no text layer (a scan) yields an empty string — the caller says so
 * rather than storing nothing.
 */
export async function pdfText(
  data: Uint8Array,
  pdfjs?: PdfJs,
): Promise<{ text: string; pages: number }> {
  const lib = pdfjs ?? (await browserPdfJs());
  // verbosity 0: errors only — a missing standard-font file matters for
  // drawing a page, not for reading its text.
  const doc = await lib.getDocument({ data, isEvalSupported: false, verbosity: 0 }).promise;
  try {
    const pages: string[] = [];
    for (let n = 1; n <= doc.numPages; n += 1) {
      const content = await (await doc.getPage(n)).getTextContent();
      pages.push(
        content.items
          .map((item) => {
            const { str, hasEOL } = item as { str?: string; hasEOL?: boolean };
            return typeof str === 'string' ? str + (hasEOL ? '\n' : '') : '';
          })
          .join('')
          .trim(),
      );
    }
    return { text: pages.filter((p) => p !== '').join('\n\n'), pages: doc.numPages };
  } finally {
    // Frees the parsed document in the worker; a long session reads many.
    await doc.destroy?.();
  }
}

/** The mammoth surface used here. */
export interface Mammoth {
  extractRawText(input: { arrayBuffer: ArrayBuffer }): Promise<{ value: string }>;
}

/** A .docx's text, paragraphs separated by blank lines (mammoth's raw-text output). */
export async function docxText(data: ArrayBuffer, mammoth?: Mammoth): Promise<string> {
  const lib = mammoth ?? ((await import('mammoth')) as unknown as Mammoth);
  return (await lib.extractRawText({ arrayBuffer: data })).value.trim();
}

/** Read one file into text, or say in a sentence why it could not be read. */
export async function extractDocumentText(file: File): Promise<ExtractedText> {
  const format = documentFormatOf(file.name);
  try {
    if (format === 'text') return { ok: true, text: await file.text() };
    if (format === 'docx') {
      const text = await docxText(await file.arrayBuffer());
      return text === ''
        ? { ok: false, message: `${file.name} has no text in it that Phibian can read.` }
        : { ok: true, text };
    }
    if (format === 'pdf') {
      const { text, pages } = await pdfText(new Uint8Array(await file.arrayBuffer()));
      return text === ''
        ? {
            ok: false,
            message:
              `${file.name} looks like a scan: its pages are pictures of text, with no text ` +
              'layer to read. Paste the text instead, or upload a version saved from the original.',
          }
        : { ok: true, text, pages };
    }
  } catch {
    // The library's own message can quote the file; the sentence below cannot.
    return {
      ok: false,
      message: `${file.name} could not be read. It may be damaged or password-protected.`,
    };
  }
  return {
    ok: false,
    message: `${file.name} is not a format Phibian can read. Use PDF, Word (.docx), .txt or .md, or paste the text.`,
  };
}
